/* © 2017-2026 Booz Allen Hamilton Inc. All Rights Reserved. */

import React from 'react';
import PropTypes from 'prop-types';
import { gnoment } from 'shared-ui/gnoment';
import { connect } from 'react-redux';
import get from 'lodash/get';
import isEmpty from 'lodash/isEmpty';
import mapKeys from 'lodash/mapKeys';
import set from 'lodash/set';
import sortBy from 'lodash/sortBy';

import {
    ProgressIndicator,
    Spinner,
    SecurityHelper,
    Icon,
    announce,
    NeedToKnow,
    CSRFacilityInformation,
    Box,
    Spacer,
    ContentBlock,
    Heading,
    withLDConsumer,
    Alert,
} from 'sarsaparilla';
import { withCompatRouter, COMPAT_ROUTER_PROP_TYPES } from 'sarsaparilla/legacy';

import {
    OrderDetailsLayout,
    OrderDetailsSection,
    OrderDetailsSummary,
} from 'shared-ui/components/OrderDetailsLayout';
import { gaReportingCartReservationsForStepper } from 'shared-ui/utils/gaCommon';

import { updateAccount } from 'ui-account/src/actions/profileActions';

import { authModalActions } from 'shared-ui/auth/redux';
import {
    GROUP_DETAIL_ADDRESS_PROP_PATH,
    GROUP_DETAIL_PROP_PATH,
    GROUP_DETAIL_ADDRESS1_PROP_PATH,
    GROUP_DETAIL_ADDRESS2_PROP_PATH,
    GROUP_DETAIL_ALT_PROP_PATH,
    GROUP_DETAIL_CITY_PROP_PATH,
    GROUP_DETAIL_COUNTRY_PROP_PATH,
    GROUP_DETAIL_EMAIL_PROP_PATH,
    GROUP_DETAIL_FIRST_NAME_PROP_PATH,
    GROUP_DETAIL_LAST_NAME_PROP_PATH,
    GROUP_DETAIL_PHONE_PROP_PATH,
    GROUP_DETAIL_SAVE_PROFILE_PROP_PATH,
    GROUP_DETAIL_STATE_PROP_PATH,
    GROUP_DETAIL_ZIPCODE_PROP_PATH,
} from '../../shared-components/constants/propPaths';
import GroupDetailHOC from '../../shared-components/registration/GroupDetailNew';
import AlternatePermitHolderEntry from '../../shared-components/registration/AlternatePermitHolderEntry';
import SimpleAlternatePermitHolderEntry from '../../shared-components/registration/AlternatePermitHolderNameEmailEntry';
import AlternatePermitHolderHOC from '../../shared-components/registration/AlternatePermitHolder';
import * as sharedActions from '../../shared-components/actions/actions';
import { selectors as sharedSelectors } from '../../shared-components/reducers/index.js';

import ModifyGroupSizeModalContainer from './ModifyGroupSizeModalContainer';
import {
    loadPermitMapping,
    permitContentSelector,
} from '../../shared-components/slices/permitContent';

// Components:
import GenericList from '../components/registration/GenericList';
import GenericCommentBox from '../components/registration/GenericCommentBox';
import GuideDetails from '../components/registration/GuideDetails';
import CommercialGuideList from '../components/registration/CommercialGuideList';
import EmergencyContact from '../components/registration/EmergencyContact';
import FixedWatercraftList from '../components/registration/FixedWatercraftList';
import GroupMemberDetail from '../components/registration/GroupMemberDetail';
import HouseboatDetail from '../components/registration/HouseboatDetail';
import IssueStationDetail from '../components/registration/IssueStationDetail';
import LaunchPointDetail from '../components/registration/LaunchPointDetail';
import PermitHolderIdentification from '../components/registration/PermitHolderIdentification';
import PermitTripDates from '../components/registration/PermitTripDates';
import PreferenceDetail from '../components/registration/PreferenceDetail';
import ReservationDetail from '../components/registration/ReservationDetail';
import SnowmobileCounter from '../components/registration/SnowmobileCounter';
import TravelMethod from '../components/registration/TravelMethod';
import VehicleList from '../components/registration/VehicleList';
import WatercraftList from '../components/registration/WatercraftList';

// Constants/Utils:
import * as types from '../constants';
import * as pActions from '../actions/permit';
import * as SharedConstants from '../constants/shared-constants';
import * as PROPS from '../props/permit';

import {
    animalTypeToString,
    calculateEntrancesExits,
    calculateIssueStations,
    calculateRemainingIssuanceFee,
    dateDiffs,
    errorsToStringList,
    generateLineItems,
    getFirstDivision,
    getLaunchWindowOptions,
    getNewId,
    getRecareaName,
    getValidationErrorStyle,
    hasLotteryPreferenceDateDivisionGroupSize,
    hasLotteryPreferenceDateRangeGroupSize,
    hasRule,
    isMobile,
    issuanceStatuses,
    loadFeesScript,
    loadPassInfo,
    nightsInRange,
    readRule,
    readRuleValue,
    runValidations,
    scrollUp,
    setButtonLabels,
    getDefaultPass,
    entranceHasParking,
    isSnowmobile,
    isVehicle,
    equipmentTypes,
    isCampsiteAssigned,
    getMaxMemberQuota,
    tempUpdatePermitWithAggSeasonRules,
    getSeasonality,
    SEASONALITY_HIGHUSESEASON,
    feeCalculationModels,
    isInvalidLotteryAward,
} from '../utils/utilsNew';

import {
    convertIssuanceToAnimalObj,
    convertIssuanceToCampsitePreferenceObj,
    convertIssuanceToGroupDetailObj,
    convertIssuanceToGroupMembersObj,
    convertIssuanceToGuidesObj,
    convertIssuanceToIssueStationDetailObj,
    convertIssuanceToLaunchDetailObj,
    convertIssuanceToWatercraftObj,
    fetchGuides,
    fetchCampsitePreferences,
    setGroupDetails,
    getResizedGroupInfo,
    modifyIssuanceGroupSize,
} from '../utils/issuance-utilsNew';

const PROGRESS_STEPS = ['Order Details', 'Review Cart', 'Payment', 'Confirmation'];

const dataBindings = {
    [GROUP_DETAIL_ADDRESS_PROP_PATH]: 'groupLeader.home_address',
    [GROUP_DETAIL_PROP_PATH]: 'groupLeader',

    [GROUP_DETAIL_ADDRESS1_PROP_PATH]: 'address1',
    [GROUP_DETAIL_ADDRESS2_PROP_PATH]: 'address2',
    [GROUP_DETAIL_ALT_PROP_PATH]: 'altLeaders',
    [GROUP_DETAIL_CITY_PROP_PATH]: 'city',
    [GROUP_DETAIL_COUNTRY_PROP_PATH]: 'country',
    [GROUP_DETAIL_EMAIL_PROP_PATH]: 'email',
    [GROUP_DETAIL_FIRST_NAME_PROP_PATH]: 'first_name',
    [GROUP_DETAIL_LAST_NAME_PROP_PATH]: 'last_name',
    [GROUP_DETAIL_PHONE_PROP_PATH]: 'cell_phone',
    [GROUP_DETAIL_SAVE_PROFILE_PROP_PATH]: 'save_profile',
    [GROUP_DETAIL_STATE_PROP_PATH]: 'state',
    [GROUP_DETAIL_ZIPCODE_PROP_PATH]: 'zip_code',
};

const GroupDetailNew = GroupDetailHOC(PROPS.ISSUANCE, dataBindings);
const AlternatePermitHolder = AlternatePermitHolderHOC(
    AlternatePermitHolderEntry,
    PROPS.ISSUANCE,
    dataBindings
);
const SimpleAlternatePermitHolder = AlternatePermitHolderHOC(
    SimpleAlternatePermitHolderEntry,
    PROPS.ISSUANCE,
    dataBindings
);

const propTypes = {
    router: COMPAT_ROUTER_PROP_TYPES,
    cart: PropTypes.shape({
        total: PropTypes.string,
    }),
    dispatch: PropTypes.func,
    campsites: PropTypes.arrayOf(PropTypes.shape({})),
    entryDate: PropTypes.shape({}),
    filters: PropTypes.shape({}),
    recarea: PropTypes.arrayOf(
        PropTypes.shape({
            name: PropTypes.string,
        })
    ),
    issuanceError: PropTypes.shape({}),
    fetchError: PropTypes.shape({}),
    cartError: PropTypes.shape({}),
    permit: PROPS.PERMIT,
    permitValidation: PropTypes.objectOf(PropTypes.object),
    issuance: PROPS.ISSUANCE,
    stayLimit: PropTypes.shape({}),
    updateIssuanceResult: PropTypes.string,
    updatedIssuance: PropTypes.shape({}),
    all_guides: PropTypes.array,
    aggSeasonRules: PropTypes.array,
    permitMapping: PropTypes.shape({}),
    flags: PropTypes.object,
};

const defaultProps = {
    entryDate: gnoment().utc(),
    cart: {
        expiration: new Date(),
    },
    recarea: [],
    all_guides: [],
    aggSeasonRules: [],
};

class PermitRegistrationPage extends React.Component {
    static propTypes = propTypes;
    static defaultProps = defaultProps;

    constructor(props) {
        super(props);
        const entryDate = props.entryDate ? gnoment(props.entryDate) : gnoment().utc();
        const accountFromSession = SecurityHelper.getAccount();

        this.errorsRef = React.createRef();

        this.state = {
            amountDue: 0,
            lineItems: [],
            errors: [],
            fetching: false,
            guides: [],
            purchasing_user: {
                first_name: get(accountFromSession, 'first_name'),
                last_name: get(accountFromSession, 'last_name'),
                email: get(accountFromSession, 'email'),
                phone:
                    get(accountFromSession, 'cell_phone') ||
                    get(accountFromSession, 'home_phone'),
                user_id: get(accountFromSession, 'account_id'),
            },
            purchasing_user_address: {
                street_address1: get(accountFromSession, 'home_address.address1'),
                street_address2: get(accountFromSession, 'home_address.address2'),
                city: get(accountFromSession, 'home_address.city'),
                country_code: get(accountFromSession, 'home_address.country'),
                state_code: get(accountFromSession, 'home_address.state'),
                postal_code: get(accountFromSession, 'home_address.zip_code'),
            },
            groupDetail: {
                groupLeaderType: 'primary-leader',
                groupLeader: {
                    ...accountFromSession,
                    home_address: { ...accountFromSession.home_address },
                },
                otherLeader: {
                    email: '',
                },
                altLeaders: [],
            },
            groupMembers: [],
            emergencyContact: {
                first_name: '',
                last_name: '',
                phone: '',
            },
            houseboat: {
                first_name: '',
                last_name: '',
                registration_number: '',
            },
            launchDetail: {
                entryDate,
                exitDate: null,
                entryLocationId: '',
                exitLocationId: '',
                isExtended: false,
                launchTime: null,
            },
            issueStationDetail: {
                selectedStationId: '',
            },
            animals: [],
            animalsTotal: 0,
            maxAnimalQuota: null,
            pets: [],
            petsTotal: 0,
            maxPetQuota: null,
            maxMemberQuota: null,
            maxPartyQuota: null,
            watercrafts: [],
            watercraftsCount: 0,
            maxWatercraftQuota: null,
            snowmobiles: 0,
            comment: '',
            priorExperience: '',
            division: {},
            validation: {},
            validation_rules: {},
            needToKnowChecked: false,
            campsitePreferences: {},
            validIssueStations: [],
            validEntrances: [],
            validExits: [],
            vehicles: [],
            watercraftDetails: '',
            isModifyGroupSizeOpen: false,
            isModifyDesolation: false,
        };
    }

    componentDidMount() {
        const loggedin = SecurityHelper.isLoggedIn();
        if (!loggedin) {
            this.props.dispatch(authModalActions.openLoginModal());
            return;
        }

        const reservationId = this.props.router.params.reservationId;
        const issuanceId = reservationId.split(types.RES_ID_DELIMITER)[0];
        const permitId = this.props.router.params.permitId;
        const apiCalls = [
            this.props.dispatch(pActions.fetchPermit(permitId)),
            this.props.dispatch(sharedActions.fetchReservationSummaryImageUrl(permitId)),
            this.props.dispatch(pActions.fetchAggregateSeason(permitId)),
            loadFeesScript(),
        ];
        Promise.all(apiCalls).then(() => {
            let recId = 0;
            if (get(this.props, 'permit.recareas[0]')) {
                recId = this.props.permit.recareas[0];
                this.props.dispatch(pActions.fetchRecarea(recId));
            }
            this.setState({
                validation_rules: mapKeys(
                    get(this.props, 'permit.validations', {}),
                    'name'
                ),
            });

            this.props
                .dispatch(pActions.fetchUnpaidIssuance(issuanceId, permitId))
                .then((newIssuance) => {
                    // @todo this should be done as an effect
                    const issuance = newIssuance;
                    const { permit } = this.props;
                    this.props.dispatch(pActions.fetchStayLimitRule(issuance));

                    if (SecurityHelper.isCommercialAccount()) {
                        this.props.dispatch(pActions.fetchAccountEmployees());
                    }

                    if (!get(issuance, 'permit_holder.first_name')) {
                        // Since we don't have permit holder info already set on the issuance, grab it from the session account
                        const accountFromSession = SecurityHelper.getAccount();
                        issuance.permit_holder = {
                            first_name: get(accountFromSession, 'first_name'),
                            last_name: get(accountFromSession, 'last_name'),
                            email: get(accountFromSession, 'email'),
                            phone:
                                get(accountFromSession, 'cell_phone') ||
                                get(accountFromSession, 'home_phone'),
                            user_id: get(accountFromSession, 'account_id'),
                        };
                        const address = accountFromSession.home_address || {};
                        issuance.permit_holder_address = {
                            street_address1: get(address, 'address1'),
                            street_address2: get(address, 'address2'),
                            city: get(address, 'city'),
                            country: get(address, 'country_code'),
                            state_code: get(address, 'state'),
                            postal_code: get(address, 'zip_code'),
                        };
                    }

                    const equipments = get(issuance, 'equipments', []);
                    const watercrafts = convertIssuanceToWatercraftObj(equipments);
                    const animals = convertIssuanceToAnimalObj(issuance.animals);
                    const pets = convertIssuanceToAnimalObj(issuance.pets);
                    const snowmobileEquipment = equipments.find((equipment) =>
                        isSnowmobile(equipment.type)
                    );
                    const vehicleEquipment = equipments.filter((equipment) =>
                        isVehicle(equipment.type)
                    );
                    const guides = convertIssuanceToGuidesObj(issuance.guides);

                    this.handleGroupDetailChange(
                        convertIssuanceToGroupDetailObj(issuance, this.state.groupDetail)
                    );
                    this.handleIssueStationDetailChange(
                        convertIssuanceToIssueStationDetailObj(
                            issuance.pickup_location_id
                        )
                    );
                    this.handleWatercraftsChange(
                        watercrafts,
                        this.computeWatercraftTotal(watercrafts)
                    );
                    this.handleSnowmobilesChange(get(snowmobileEquipment, 'qty') || 0);
                    this.handlePetsChange(pets, this.computePetTotal(issuance.pets));
                    this.handleAnimalsChange(
                        animals,
                        this.computeAnimalTotal(issuance.animals)
                    );
                    this.handleLaunchDetailChange(
                        convertIssuanceToLaunchDetailObj(issuance)
                    );
                    this.handlePreferenceChange(
                        convertIssuanceToCampsitePreferenceObj(
                            issuance.campsite_preferences
                        )
                    );
                    this.handlePriorExperienceChange(issuance.prior_experience);
                    this.handleWatercraftDetailsChange(issuance.watercraft_details);
                    this.handleGuidesChange(guides);
                    this.handleCommentChange(issuance.comment);
                    this.handleVehiclesChange(vehicleEquipment);

                    if (
                        issuance.status ===
                            issuanceStatuses.get(types.PERMIT_PRELIMINARY) ||
                        issuance.status === issuanceStatuses.get(types.LOTTERY_AWARDED)
                    ) {
                        // a prelim permit wouldn't have this - putting here for convenience
                        const lotteryId = get(issuance, 'lottery_id', false);
                        if (lotteryId) {
                            this.props.dispatch(pActions.fetchLottery(lotteryId));
                        }
                    }

                    let newGroup = [];
                    // include anything here that can be loaded from the quota filters, default to adults since we don't know yet.
                    if (issuance.group_info?.length) {
                        newGroup = convertIssuanceToGroupMembersObj(issuance.group_info);
                        // Some permits (e.g. 234623) return group_info members without end_date.
                        // Default endDate to the issuance end_date so validation doesn't fail.
                        if (issuance.end_date) {
                            const fallbackEndDate = gnoment
                                .utc(issuance.end_date)
                                .startOf('day');
                            newGroup = newGroup.map((member) => ({
                                ...member,
                                endDate: member.endDate || fallbackEndDate,
                            }));
                        }
                        this.handleGroupMemberChange(newGroup);
                    } else {
                        // Pass a freshly constructed launchDetail with exitDate already populated
                        // rather than this.state.launchDetail which is still null at this point
                        // due to React's batched setState.
                        this.loadGroupMembers(
                            this.props.permit,
                            issuance,
                            convertIssuanceToLaunchDetailObj(issuance)
                        );
                    }

                    if (!issuance.division_id) {
                        const initDivision = this.getDivision();
                        this.setState({ division: initDivision });
                    } else {
                        this.setState(
                            {
                                division:
                                    this.props.permit.divisions[issuance.division_id],
                            },
                            () => {
                                this.updateIssuanceFee();
                            }
                        );
                    }

                    this.setState((state) => {
                        const { launchDetail } = state;

                        if (issuance.start_date) {
                            launchDetail.entryDate = gnoment(issuance.start_date).utc();
                        } else {
                            launchDetail.entryDate = gnoment(this.props.entryDate).utc();
                        }
                        // the exit date picker will default to the start date because the backend returns an exitDate the
                        // same as startDate if no exit date has been picked yet. Now we check if that breaks the stayMinimum rule
                        const stayMinimum = parseInt(
                            readRuleValue(
                                'StayMinimum',
                                this.props.permit.rules,
                                issuance.division_id
                            ),
                            10
                        );
                        const issueStartDate = gnoment(issuance.start_date).utc();
                        const issueEndDate = issuance.end_date
                            ? gnoment(issuance.end_date).utc()
                            : undefined;

                        if (
                            stayMinimum &&
                            issueEndDate &&
                            issueEndDate.isBefore(
                                issueStartDate.clone().add(stayMinimum, 's')
                            )
                        ) {
                            launchDetail.exitDate = issueStartDate
                                .clone()
                                .add(stayMinimum, 's');
                        }

                        launchDetail.isExtended = issuance.is_extended;

                        let lastDivisionId;
                        const itinerary = this.props.issuance.itinerary;

                        // If Olympic or Pere Marquette, pass in the final division id so the exits will be filtered
                        // based on the that last division instead of the first one.
                        if (get(itinerary, 'length', 0) > 1) {
                            lastDivisionId = itinerary[itinerary.length - 1].division_id;
                        }

                        return calculateEntrancesExits(
                            this.props.permit.entrances,
                            launchDetail,
                            this.props.issuance.division_id,
                            this.props.permit.divisions,
                            lastDivisionId
                        );
                    });

                    // Assign group leader as the first group member only if there are no group members assigned
                    // Only applicable for private accounts. Commercial accounts should not have group leader as first group members
                    if (!SecurityHelper.isCommercialAccount()) {
                        const newGroupMembers = Object.assign([], newGroup);
                        if (newGroup.length === 0) {
                            newGroupMembers[0] = {
                                fName:
                                    this.state.groupDetail.groupLeader.first_name || '',
                                mName:
                                    this.state.groupDetail.groupLeader.middle_initial ||
                                    '',
                                lName: this.state.groupDetail.groupLeader.last_name || '',
                                id: getNewId(),
                                remark: '',
                                removable: false,
                                startDate: gnoment
                                    .utc(this.props.issuance.start_date)
                                    .startOf('day'),
                                endDate: this.props.issuance.end_date
                                    ? gnoment
                                          .utc(this.props.issuance.end_date)
                                          .startOf('day')
                                    : gnoment
                                          .utc(this.props.issuance.start_date)
                                          .startOf('day'),
                                passholderType: getDefaultPass(permit),
                            };

                            this.setState({ groupMembers: newGroupMembers });

                            this.updateIssuanceFee({
                                ...this.fetchMembers(newGroupMembers),
                            });
                        }

                        this.handleIsCommerciallyGuidedChange(
                            issuance.is_commercially_guided,
                            get(guides, '0')
                        );
                    }

                    this.setState({ needToKnowChecked: issuance.need_to_know_checked });

                    if (this.props.permit.components.travel_method_list) {
                        this.setState({ travelMethodType: issuance.travel_method_type });
                    }

                    if (this.props.permit.components.emergency_contact) {
                        this.setState({ emergencyContact: issuance.emergency_contact });
                    }

                    if (this.props.permit.components.houseboat_list) {
                        if (
                            issuance.houseboat.type === types.PRIVATE_HOUSEBOAT_OWNER_TYPE
                        ) {
                            this.setState({ isPrivateHouseboat: true });
                        }
                        this.setState({ houseboat: issuance.houseboat });
                    }

                    if (this.props.permit.entrances) {
                        this.setState(
                            calculateIssueStations(
                                this.props.permit.entrances,
                                this.props.issuance.division_id
                            )
                        );
                    }

                    this.updateRulesState(this.props);
                })
                .catch(() => {
                    // avoid throwing error up
                    // reservation failed to load, will be handled by reducer GET_ISSUANCE_ERROR
                });
        });
        this.props.dispatch(loadPermitMapping());
        this.props.dispatch(pActions.fetchCart());
        this.props.dispatch(pActions.fetchCampsites(permitId));
        const maxWatercraftQuota = this.getMaxWatercraftQuota(
            this.props.filters,
            this.props.permit,
            this.props.issuance
        );

        if (maxWatercraftQuota) {
            this.setState({ maxWatercraftQuota: parseInt(maxWatercraftQuota, 10) });
        }
    }

    componentDidUpdate(prevProps, prevState) {
        if (
            get(this.props, 'permit.components.watercraft_list') &&
            !this.state.watercrafts.length
        ) {
            const craft = {
                id: 1,
                type: '0',
                amount: 0,
                removable: true,
            };
            this.setState({ watercrafts: [craft], watercraftsCount: 0 });
        }

        const { filters, permit, issuance } = this.props;

        const maxWatercraftQuota = this.getMaxWatercraftQuota(filters, permit, issuance);
        if (
            maxWatercraftQuota &&
            (prevState.maxWatercraftQuota !== this.state.maxWatercraftQuota ||
                !this.state.maxWatercraftQuota)
        ) {
            this.setState({ maxWatercraftQuota: parseInt(maxWatercraftQuota, 10) });
        }

        if (issuance.id !== prevProps.issuance.id || prevProps.permit.id !== permit.id) {
            this.setState(
                (state) => {
                    return calculateEntrancesExits(
                        permit.entrances,
                        state.launchDetail,
                        issuance.division_id,
                        permit.divisions
                    );
                },
                () => this.updateIssuanceFee()
            );

            this.setState(calculateIssueStations(permit.entrances, issuance.division_id));

            this.updateRulesState(this.props);
        }

        const allowModRefundReload = get(
            this.props.flags,
            'legacyPermitDesoModRefundReload',
            false
        );
        if (
            allowModRefundReload &&
            this.state.isModifyDesolation &&
            this.state.amountDue <= 0
        ) {
            window.location.assign(
                `/account/orders/${issuance.order_id}/reservations/${issuance.id}__${permit.id}?updateSuccessful=true`
            );
        }
    }

    componentWillUnmount() {
        const { dispatch } = this.props;

        dispatch(pActions.clearUpdateIssuanceError());
        dispatch(pActions.clearFetchError());
        dispatch(pActions.clearCartError());
    }

    getMaxWatercraftQuota(filters, permit, issuance) {
        const maxWatercraftQuota = get(filters, types.WATERCRAFT_AVAILABILITY_KEY_NAME);
        if (maxWatercraftQuota) {
            return maxWatercraftQuota;
        }

        const maxWatercraftPerReservationRule = readRule(
            types.MAX_WATERCRAFT_PER_RESERVATION,
            permit.rules
        );
        const hasWatercraftPerReservationRule =
            !maxWatercraftPerReservationRule.isMissing;

        if (hasWatercraftPerReservationRule) {
            // if watercraft counts towards quota, the count is predetermined in detailed avail
            if (
                hasRule(types.WATERCRAFT_AVAILABILITY_KEY_NAME, permit.rules) &&
                get(issuance, 'equipments')
            ) {
                return this.state.watercraftsCount;
            }
            return maxWatercraftPerReservationRule.value;
        }

        return undefined;
    }

    getStatus() {
        if (this.props.issuance.status) {
            if (
                this.props.issuance.status !==
                issuanceStatuses.get(types.PERMIT_PRELIMINARY)
            ) {
                return this.props.issuance.status;
            }
        }
        return issuanceStatuses.get(types.PERMIT_IN_PROGRESS);
    }

    getDivision = () => {
        let divisionInIssuance;
        if (this.props.issuance.division_id) {
            divisionInIssuance =
                this.props.permit.divisions[this.props.issuance.division_id];
        }
        if (!divisionInIssuance) {
            return getFirstDivision(this.props.permit.divisions);
        }
        return divisionInIssuance;
    };

    getName() {
        const { permit } = this.props;
        if (permit?.name) {
            return `${permit?.name} - Permit`;
        }
        return 'Permit';
    }

    getRemainingMembersAllowed() {
        const { maxMemberQuota, maxPartyQuota, petsTotal, animalsTotal } = this.state;
        if (Number.isInteger(maxMemberQuota)) {
            if (Number.isInteger(maxPartyQuota)) {
                const remainingMembersAllowed = maxPartyQuota - animalsTotal - petsTotal;
                return maxMemberQuota > remainingMembersAllowed
                    ? remainingMembersAllowed
                    : maxMemberQuota;
            }
            return maxMemberQuota;
        }
        return undefined;
    }

    getRemainingStockAllowed() {
        const { maxPartyQuota, maxAnimalQuota, petsTotal, groupMembers } = this.state;
        if (Number.isInteger(maxAnimalQuota)) {
            if (Number.isInteger(maxPartyQuota)) {
                const remainingStockAllowed =
                    maxPartyQuota - petsTotal - groupMembers.length;
                return maxAnimalQuota > remainingStockAllowed
                    ? remainingStockAllowed
                    : maxAnimalQuota;
            }
            return maxAnimalQuota;
        }
        return null;
    }

    getRemainingPetsAllowed() {
        const { maxPartyQuota, maxPetQuota, animalsTotal, groupMembers } = this.state;
        if (Number.isInteger(maxPetQuota)) {
            if (Number.isInteger(maxPartyQuota)) {
                const remainingPetsAllowed =
                    maxPartyQuota - animalsTotal - groupMembers.length;
                return maxPetQuota > remainingPetsAllowed
                    ? remainingPetsAllowed
                    : maxPetQuota;
            }
            return maxPetQuota;
        }
        return null;
    }

    updateRulesState(nextProps) {
        const divisionID = get(nextProps, 'issuance.division_id');
        const rules = get(nextProps, 'permit.rules', []);

        let maxMemberQuota = getMaxMemberQuota(nextProps);

        if (hasRule(types.REDUCIBLE_GROUP_SIZE_KEY_NAME, rules)) {
            maxMemberQuota = get(nextProps.issuance, 'group_info', []).length;
        }
        if (maxMemberQuota) {
            this.setState({ maxMemberQuota });
        }

        const maxAnimalQuota = readRuleValue(types.MAX_STOCK_KEY_NAME, rules, divisionID);
        if (maxAnimalQuota) {
            this.setState({ maxAnimalQuota });
        }

        const maxPetQuota = readRuleValue(types.MAX_PETS_KEY_NAME, rules, divisionID);
        if (maxPetQuota) {
            this.setState({ maxPetQuota });
        }

        const maxPartyQuota = readRuleValue(
            types.MAX_PARTY_SIZE_KEY_NAME,
            rules,
            divisionID
        );
        if (maxPartyQuota) {
            this.setState({ maxPartyQuota });
        }

        const maxGroupSize = readRuleValue(
            types.MAX_GROUP_SIZE_KEY_NAME,
            rules,
            divisionID
        );
        if (maxGroupSize) {
            this.setState({ maxGroupSize });
        }
        const maxGroupSizeCommercial = readRuleValue(
            types.MAX_GROUP_SIZE_COMMERCIAL_KEY_NAME,
            rules,
            divisionID
        );
        if (maxGroupSizeCommercial) {
            this.setState({ maxGroupSizeCommercial });
        }

        const disableGroupList = hasRule('DisableGroupList', rules);
        if (disableGroupList) {
            // need to set maxGroupSize to what is in the issuance.
            this.setState({ maxGroupSize: nextProps.issuance.group_size });

            // and commercial?
            // this.setState({ maxGroupSizeCommercial: nextProps.issuance.group_size });
        }

        const guidesCountTowardsGroupSize = hasRule(
            types.GUIDES_COUNT_TOWARDS_GROUP_SIZE,
            rules
        );
        const quotaByMemberDaily = hasRule(
            types.MEMBER_AVAILABILITY_DAILY_KEY_NAME,
            rules,
            divisionID
        );
        const quotaByMember = hasRule(
            types.MEMBER_AVAILABILITY_KEY_NAME,
            rules,
            divisionID
        );

        if ((quotaByMemberDaily || quotaByMember) && guidesCountTowardsGroupSize) {
            this.setState({ guidesCountTowardsQuota: true });
        }

        this.setState({
            disableModifyGroupList: hasRule(types.DISABLE_MODIFY_GROUP_LIST, rules),
        });
    }

    loadGroupMembers = (permit, issuance, launchDetail) => {
        let groupMembers = issuance.group_info;
        let maxGroupSize = readRuleValue('MaxGroupSize', permit.rules);
        // if processing lottery won application with group size data - don't process a completed reservation that came from a lottery
        if (
            get(this.props, 'issuance.status') !==
                issuanceStatuses.get(types.PERMIT_COMPLETE) &&
            issuance.lottery_awarded &&
            (hasLotteryPreferenceDateDivisionGroupSize(permit, issuance.division_id) ||
                hasLotteryPreferenceDateRangeGroupSize(permit, issuance.division_id))
        ) {
            const groupSize = get(issuance, 'selected_lottery_preference.group_max_size');
            if (groupSize) {
                groupMembers = setGroupDetails(groupSize, launchDetail);
                maxGroupSize = groupSize;
            }
            // Set the upper bounds on number of group members that can be selected
            this.setState({ maxMemberQuota: parseInt(maxGroupSize, 10) });
        }

        this.handleGroupMemberChange(convertIssuanceToGroupMembersObj(groupMembers));
    };

    isThisFormValid = () => {
        const validation = {
            ...this.permitHolderValidation(),
            ...this.commerciallyGuidedValidation(),
            ...this.props.permitValidation,
        };
        const errors = Object.values(validation);
        this.setState((prevState) => {
            if (prevState.validationEnabled) return { errors, validation };
            return {};
        });
        return errors.length === 0;
    };

    isQuotaModifyAllowed() {
        const { rules, id } = this.props.permit;
        // We want to remove the Modify Date link from the following Permits based on R1S-20172
        const exclusionMap = [
            types.DESOLATION_GRAY_ID,
            types.SALMON_RIVER_ID,
            types.MIDDLE_FORK_ID,
            types.SELWAY_ID,
            types.SALT_ID,
            types.HELLS_CANYON_ID,
        ];
        if (exclusionMap.includes(id)) {
            return false;
        }
        const entryDate = gnoment(this.state.launchDetail.entryDate).utc();
        const deadlineRule = readRule('ModificationDeadline', rules);

        let deadlineRuleValue = deadlineRule.value;
        if (deadlineRule.isMissing) {
            deadlineRuleValue = readRuleValue('CancellationDeadline', rules);
        }
        let changesAllowed = false;
        if (typeof deadlineRuleValue === 'number') {
            const currentDate = gnoment().utc();
            const daysUntilReservation = entryDate.diff(currentDate, 'days');
            if (daysUntilReservation >= deadlineRuleValue) {
                changesAllowed = true;
            }
        }
        return (
            changesAllowed ||
            this.props.issuance.status ===
                issuanceStatuses.get(types.PERMIT_PRELIMINARY) ||
            this.props.issuance.status === issuanceStatuses.get(types.PERMIT_IN_PROGRESS)
        );
    }

    permitHolderValidation = () => {
        const { groupLeader } = this.state.groupDetail;
        const reservationId = `${this.props.issuance.id}${types.RES_ID_DELIMITER}${this.props.permit.id}`;

        const phValidations = {
            invalidEmail: !groupLeader.email,
            invalidFirstName: !groupLeader.first_name,
            invalidLastName: !groupLeader.last_name,
            invalidPhone: !groupLeader.cell_phone,
            invalidAddress1: !groupLeader.home_address.address1,
            invalidCity: !groupLeader.home_address.city,
            invalidCountry: !groupLeader.home_address.country,
            invalidState: !groupLeader.home_address.state,
            invalidZipCode: !groupLeader.home_address.zip_code,
        };

        // map errors to new GroupDetails HOC
        const requiredFields = {
            email: 'invalidEmail',
            first_name: 'invalidFirstName',
            last_name: 'invalidLastName',
            cell_phone: 'invalidPhone',
            'home_address.address1': 'invalidAddress1',
            'home_address.city': 'invalidCity',
            'home_address.country': 'invalidCountry',
            'home_address.state': 'invalidState',
            'home_address.zip_code': 'invalidZipCode',
        };
        Object.keys(requiredFields).forEach((path) => {
            phValidations[`${reservationId}_groupLeader.${path}`] =
                phValidations[requiredFields[path]];
            phValidations[`${reservationId}_groupLeader.${path}_req`] =
                phValidations[requiredFields[path]];
        });

        this.setState({ phValidations });
        return {};
    };

    commerciallyGuidedValidation = () => {
        if (
            !get(this.state, `validation_rules.[${types.IsCommerciallyGuidedEmpty}]`) ||
            !get(this.state, 'isCommerciallyGuided')
        ) {
            return {};
        }

        const INVALID = {
            GUIDE_FIELDS_MISSING: (
                <div className="permit-validation">
                    Please fill out all required Commercial Guide fields.
                </div>
            ),
        };
        const guide = get(this.state, 'guides[0]', {});
        const guideValidations = {
            invalidOrgName: !guide.org_name,
        };
        this.setState({ guideValidations });

        if (!guide.org_name) return INVALID;
        return {};
    };

    computeWatercraftTotal(watercrafts) {
        return watercrafts.reduce((total, watercraft) => {
            if (!watercraft.type && watercraft.type !== 0) return total;
            return total + parseInt(watercraft.amount, 10);
        }, 0);
    }

    computeAnimalTotal(animals) {
        if (animals) {
            return animals.reduce((total, animal) => {
                return total + parseInt(animal.amount, 10);
            }, 0);
        }
        return null;
    }

    computePetTotal(pets) {
        if (pets) {
            return pets.reduce((total, pet) => {
                return total + parseInt(pet.amount, 10);
            }, 0);
        }
        return null;
    }

    handleGroupDetailChange = (groupDetail) => this.setState({ groupDetail });

    handleGroupMemberChange = (
        groupMembers,
        updateLineItems = true,
        callback = () => {}
    ) => {
        // update lineItems & amountDue
        if (updateLineItems) {
            this.updateIssuanceFee({ ...this.fetchMembers(groupMembers) });
        }

        this.setState({ groupMembers }, callback);
    };

    handleVehiclesChange = (vehicles) => this.setState({ vehicles });

    handleWatercraftsChange = (watercrafts, watercraftsCount) => {
        // update lineItems & amountDue
        this.updateIssuanceFee({ ...this.fetchEquipments(watercrafts) });

        if (watercraftsCount || watercraftsCount === 0) {
            this.setState({ watercrafts, watercraftsCount });
        } else {
            this.setState({ watercrafts });
        }
    };

    handlePetsChange = (pets, petsTotal) => {
        this.setState((prevState) => ({
            pets,
            petsTotal: Number.isInteger(petsTotal) ? petsTotal : prevState.petsTotal,
        }));
    };

    handleAnimalsChange = (animalsList, animalsTotal) => {
        this.setState((prevState) => ({
            animals: animalsList,
            animalsTotal: Number.isInteger(animalsTotal)
                ? animalsTotal
                : prevState.animalsTotal,
        }));
    };

    handleIssueStationDetailChange = (e) => {
        if (e.selectedStationId) {
            this.setState({ issueStationDetail: e });
        }
    };

    handleHouseboatChange = (houseboat) => {
        if (houseboat.type === types.PRIVATE_HOUSEBOAT_OWNER_TYPE) {
            this.setState({ isPrivateHouseboat: true });
        } else {
            this.setState({ isPrivateHouseboat: false });
        }
        this.setState({ houseboat });
    };

    handleEmergencyContactChange = (emergencyContact) =>
        this.setState({ emergencyContact });

    handleTravelMethodChange = (travelMethodType) => this.setState({ travelMethodType });

    handleLaunchDetailChange = (e, updateLineItems = true) => {
        const groupMembers = this.state.groupMembers.map((member, index) => {
            if (index === 0 && !SecurityHelper.isCommercialAccount()) {
                return { ...member, endDate: e.exitDate };
            }

            const newMember = { ...member };
            const memberEndDate = get(member, 'endDate', null);
            if (memberEndDate !== null && memberEndDate.isAfter(e.exitDate)) {
                newMember.endDate = e.exitDate;
            }
            const memberStartDate = get(member, 'startDate', null);
            if (memberStartDate !== null && memberStartDate.isAfter(e.exitDate)) {
                newMember.startDate = e.exitDate;
            }
            return newMember;
        });

        // update lineItems & amountDue
        if (updateLineItems) {
            this.updateIssuanceFee({
                ...this.fetchMembers(groupMembers),
                start_date: e.entryDate,
                end_date: e.exitDate ? e.exitDate : e.entryDate,
            });
        }

        this.setState({ launchDetail: e, groupMembers });

        if (this.disableAnimalList()) {
            this.handleAnimalsChange([]);
        }
    };

    handleCommentChange = (e) => this.setState({ comment: e });

    handlePriorExperienceChange = (e) => this.setState({ priorExperience: e });

    handleWatercraftDetailsChange = (e) => this.setState({ watercraftDetails: e });

    handlePreferenceChange = (campsitePreferences) =>
        this.setState({ campsitePreferences });

    handleSnowmobilesChange = (e) => this.setState({ snowmobiles: Number(e) || 0 });

    handleNeedToKnowCheckboxChange = () => {
        this.setState(
            (prevState) => ({
                needToKnowChecked: !prevState.needToKnowChecked,
                validationEnabled: true,
            }),
            () => {
                if (this.state.needToKnowChecked) {
                    this.isThisFormValid();
                    if (!isMobile.any()) {
                        scrollUp();
                    }
                }
            }
        );
    };

    handleGuidesChange = (guides, updateLineItems = true) => {
        let maxMemberQuota = getMaxMemberQuota({
            permit: this.props.permit,
            issuance: { division_id: this.props.issuance.division_id, guides },
        });

        if (hasRule(types.REDUCIBLE_GROUP_SIZE_KEY_NAME, this.props.permit.rules)) {
            maxMemberQuota = get(this.props, 'issuance.group_info', []).length;
        }
        const groupMembers = get(this.state, 'groupMembers', []);
        if (groupMembers.length > maxMemberQuota) {
            groupMembers.pop();
        }

        // update lineItems & amountDue
        if (updateLineItems) {
            this.updateIssuanceFee({
                ...this.fetchMembers(groupMembers),
                ...fetchGuides(
                    guides,
                    SecurityHelper.getAccount(),
                    this.state.isCommerciallyGuided
                ),
            });
        }

        this.setState({ guides, maxMemberQuota, groupMembers });
    };

    // if there is a commercially guided question, there's an input for a single guide
    handleIsCommerciallyGuidedChange = (isGuided, guide) => {
        if (guide) {
            this.setState({ isCommerciallyGuided: isGuided, guides: [guide] });
        } else {
            this.setState({ isCommerciallyGuided: isGuided });
        }
    };

    redirectToAvailabilityPage = () => {
        // used on PermitTripDates, EntryExitDates, LaunchPointDetail
        const reservationId = `${this.props.issuance.id}${types.RES_ID_DELIMITER}${this.props.permit.id}`;
        // Save current entrydate, exitdate, section type and groupsize
        let url = `/permits/${this.props.permit.id}/registration/detailed-availability`;

        const query = {};
        if (reservationId) {
            // this is the permit_section_filter
            query.reservationId = reservationId;
        }
        if (this.state.launchDetail.entryDate) {
            query.date = this.state.launchDetail.entryDate
                .utc()
                .startOf('day')
                .toISOString();
        }

        // Want to save the exit date on the previous page
        if (this.state.launchDetail.exitDate) {
            query.end_date = this.state.launchDetail.exitDate
                .utc()
                .startOf('day')
                .toISOString();
        }

        // Preserving the original selected permit type
        if (this.props.issuance.permit_type) {
            query.section = this.props.issuance.permit_type;
        }

        const queryString = Object.keys(query)
            .map((key) => `${encodeURIComponent(key)}=${encodeURIComponent(query[key])}`)
            .join('&');
        if (queryString) {
            url = `${url}?${queryString}`;
        }
        this.props.router.navigate(url);
    };

    onModifyDetails = () => {
        this.setState({ isModifyGroupSizeOpen: true });
    };

    fetchGroupDetails() {
        // returns object with keys 'alt_group_leaders', 'group_leader_id'... used for building issuance for POST
        const groupDetailInfo = {};
        if (this.state.groupDetail.groupLeaderType === 'primary-leader') {
            groupDetailInfo.group_leader_id = SecurityHelper.getAccount().account_id; // logged in user is group leader, here
        }

        if (this.state.groupDetail.altLeaders) {
            groupDetailInfo.alt_group_leaders = this.state.groupDetail.altLeaders;
            const altGroupLeaders = [];
            for (
                let index = 0;
                index < this.state.groupDetail.altLeaders.length;
                index++
            ) {
                const leader = this.state.groupDetail.altLeaders[index];
                altGroupLeaders.push({
                    email: leader.email,
                    is_verified: leader.is_verified,
                    is_declined: leader.is_declined,
                    first_name: leader.first_name,
                    last_name: leader.last_name,
                });
            }
            groupDetailInfo.alt_group_leaders = altGroupLeaders;
        }
        return groupDetailInfo;
    }

    getPermitHolder() {
        // returns an object with keys 'permit_holder' and 'permit_holder_address'
        // for use when sending issuance object away
        const permitHolderInfo = {};
        if (this.state.groupDetail.groupLeaderType === 'primary-leader') {
            const {
                first_name,
                last_name,
                email,
                user_id,
                cell_phone: phone,
            } = get(this.state.groupDetail, 'groupLeader', {});
            const homeAddress = get(
                this.state.groupDetail,
                'groupLeader.home_address',
                {}
            );
            const {
                address1: street_address1,
                address2: street_address2,
                city,
                country: country_code,
                state: state_code,
                zip_code: postal_code,
            } = homeAddress;
            permitHolderInfo.permit_holder = {
                first_name,
                last_name,
                email,
                user_id,
                phone,
            };
            permitHolderInfo.permit_holder_address = {
                city,
                country_code,
                postal_code,
                state_code,
                street_address1,
                street_address2,
            };
        }

        return permitHolderInfo;
    }

    updateIssuanceFee(updatedState = {}) {
        const { permit, aggSeasonRules } = this.props;
        const { division, amountDue } = this.state;
        const issuance = { ...this.createIssuanceObject(), ...updatedState };

        const lineItems = generateLineItems(
            issuance,
            division,
            tempUpdatePermitWithAggSeasonRules(permit, aggSeasonRules)
        );
        const newAmountDue = calculateRemainingIssuanceFee(
            lineItems,
            issuance.amount_paid
        );

        // Dont announce the new fee unless it has changed
        if (newAmountDue !== amountDue) {
            // from 1000 to 10, 100 to 1 or from 1011 to 10.11, 111 to 1.11
            const baseFee = newAmountDue / 100;
            // limit decimal points to the nearest hundredth as well as extending the 0s for numbers like 2.6 -> 2.60
            const [dollars, cents] = baseFee.toFixed(2).toString().split('.');
            // Split the fee into easily readable parts
            announce(`New fee is ${dollars} dollars and ${cents} cents.`);
        }
        this.setState({ lineItems, amountDue: newAmountDue });
    }

    fetchEquipments(watercrafts = this.state.watercrafts) {
        const equipmentInfo = {
            equipments: [],
        };
        watercrafts.forEach((c) => {
            equipmentInfo.equipments.push({
                type: parseInt(c.type, 10),
                qty: parseInt(c.amount, 10),
            });
        });

        if (
            entranceHasParking(
                this.state.launchDetail.entryLocationId,
                this.props.permit.entrances
            ) ||
            this.props.permit.components.vehicle_skip_entrance
        ) {
            this.state.vehicles.forEach((v) => {
                equipmentInfo.equipments.push({
                    type: v.type,
                    qty: 1,
                    description: v.description,
                    license_plate: v.license_plate,
                    license_state: v.license_state,
                });
            });
        }
        if (this.state.snowmobiles) {
            equipmentInfo.equipments.push({
                type: parseInt(equipmentTypes.get('Snowmobile'), 10),
                qty: parseInt(this.state.snowmobiles, 10),
            });
        }
        return equipmentInfo;
    }

    fetchAnimals() {
        const animalsObj = {
            animals: [],
        };
        if (this.state.animals) {
            this.state.animals.forEach((c) => {
                animalsObj.animals.push({
                    type: parseInt(c.type, 10),
                    amount: parseInt(c.amount, 10),
                });
            });
        }
        return animalsObj;
    }

    fetchPets() {
        const petsObj = {
            pets: [],
        };
        if (this.state.pets) {
            this.state.pets.forEach((c) => {
                petsObj.pets.push({
                    type: parseInt(c.type, 10),
                    amount: parseInt(c.amount, 10),
                });
            });
        }
        return petsObj;
    }

    fetchMembers(groupMembers = this.state.groupMembers) {
        const memberInfo = {
            group_info: [],
        };
        groupMembers.forEach((m) => {
            let startDate = m.startDate;
            let endDate = m.endDate;
            if (gnoment.isGnoment(m.startDate)) {
                startDate = m.startDate.utc(true).startOf('day');
            }
            if (gnoment.isGnoment(m.endDate)) {
                endDate = m.endDate.utc(true).startOf('day');
            }
            memberInfo.group_info.push({
                first_name: m.fName,
                middle_name: m.mName,
                last_name: m.lName,
                remark: m.remark,
                start_date: startDate,
                end_date: endDate,
                pass_type: m.passholderType || getDefaultPass(this.props.permit),
                passholder_id: m.passholderId,
                is_adult: m.isAdult,
                is_child: m.isChild,
                is_dog: m.isDog,
                date_of_birth: m.birthdate,
                age: m.age,
            });
        });
        return memberInfo;
    }

    addToCart = async ({ navigate = true } = {}) => {
        const { issuance, permit } = this.props;

        this.props.dispatch(pActions.clearUpdateIssuanceError());

        this.setState({ validationEnabled: true });
        if (!this.isThisFormValid()) {
            scrollUp();
            if (this.errorsRef.current) {
                this.errorsRef.current.focus();
            }
            return;
        }

        this.setState({ fetching: true });

        if (this.state.groupDetail.groupLeader.save_profile) {
            this.saveLeaderInfo();
        }
        const item = this.createIssuanceObject();

        if (item.itinerary && item.itinerary.length === 1) {
            item.itinerary[item.itinerary.length - 1].end_date = item.end_date;
        }

        await this.props.dispatch(pActions.updateIssuance(item));

        if (!navigate) this.setState({ fetching: false });

        switch (this.props.updateIssuanceResult) {
            case types.ISSUANCE_UPDATE_REQUIRES_PAYMENT:
                if (navigate) window.location.assign('/cart');
                return true;
            case types.ISSUANCE_UPDATE_REQUIRES_REFUND:
                this.props.dispatch(
                    pActions.bypassToOrderConfirmation(this.props.updatedIssuance, permit)
                );
                if (navigate) {
                    this.props.router.navigate(`/permits/${permit.id}/confirmation`);
                }
                return true;
            case types.ISSUANCE_UPDATE_SUCCESSFUL:
                if (navigate) {
                    window.location.assign(
                        `/account/orders/${issuance.order_id}/reservations/${issuance.id}__${permit.id}?updateSuccessful=true`
                    );
                }
                return true;
            case undefined:
                this.setState({ fetching: false });
                if (this.errorsRef.current) {
                    this.errorsRef.current.focus();
                }
                return;
            default:
                if (navigate) window.location.assign('/cart');
                return true;
        }
    };

    removeFromCart = () => {
        if (parseInt(this.props.cart.total, 10) > 0) {
            const issuanceId = get(this.props.issuance, 'id');
            const permitId = get(this.props.permit, 'id');
            if (issuanceId && permitId) {
                const reservationId = `${issuanceId}${types.RES_ID_DELIMITER}${permitId}`;
                this.props.dispatch(
                    pActions.cancelReservation(reservationId, this.props.router.navigate)
                );
            }
        } else {
            this.props.router.navigate(-1);
        }
    };

    saveLeaderInfo = () => {
        const acct = this.createUpdatedAccountObject(SecurityHelper.getAccount());
        this.props.dispatch(updateAccount(acct));
    };

    createUpdatedAccountObject = (prevAcct) => {
        const { cell_phone, home_address } = this.state.groupDetail.groupLeader;
        return { ...prevAcct, cell_phone, home_address };
    };

    createIssuanceObject = (issuance = this.props.issuance) => {
        let exitDate = this.state.launchDetail.exitDate;
        if (!this.state.launchDetail.exitDate) {
            exitDate = this.state.launchDetail.entryDate;
        }
        let exitPoint = this.state.launchDetail.exitLocationId;
        // Hells canyon special case where exit point gets set to the same as the entry point location if none is provided
        const resetExitPoint =
            !this.state.launchDetail.exitLocationId &&
            this.state.launchDetail.entryLocationId &&
            issuance.permit_id === types.HELLS_CANYON_POWERBOAT_ID;
        if (resetExitPoint) {
            exitPoint = this.state.launchDetail.entryLocationId;
        }
        // TODO find better way for mapping existing issuance to initItem (copying from fetched existing iss -> newly updating iss)
        const initItem = {
            ...issuance,
            division_id: this.state.division.id,
            user_id: SecurityHelper.getAccount().account_id, // This sets the user_id (purchasing user)
            status: this.getStatus(),
            user_type: 1,
            acquisition_type: 1,
            start_date: this.state.launchDetail.entryDate,
            end_date: exitDate,
            entry_location_id: this.state.launchDetail.entryLocationId,
            exit_location_id: exitPoint,
            is_extended: this.state.launchDetail.isExtended,
            launch_time: this.state.launchDetail.launchTime,
            pickup_location_id: this.state.issueStationDetail.selectedStationId,
            comment: this.state.comment,
            need_to_know_checked: this.state.needToKnowChecked,
            travel_method_type: this.state.travelMethodType,
            prior_experience: this.state.priorExperience,
            watercraft_details: this.state.watercraftDetails,
            is_commercially_guided: this.state.isCommerciallyGuided,
        };
        const groupDetail = this.fetchGroupDetails();
        const permitHolder = this.getPermitHolder();
        const memberDetail = this.fetchMembers();
        const equipmentDetail = this.fetchEquipments();
        const animalDetail = this.fetchAnimals();
        const petDetail = this.fetchPets();
        const guidesDetail = fetchGuides(
            this.state.guides,
            SecurityHelper.getAccount(),
            this.state.isCommerciallyGuided
        );
        const campsitePreferences = fetchCampsitePreferences(
            this.state.campsitePreferences,
            this.state.launchDetail.entryDate,
            this.state.launchDetail.exitDate
        );

        let item = Object.assign(
            initItem,
            groupDetail,
            permitHolder,
            memberDetail,
            equipmentDetail,
            animalDetail,
            petDetail,
            guidesDetail,
            campsitePreferences
        );
        if (get(this.props, 'permit.components.houseboat_list')) {
            item = Object.assign(item, { houseboat: this.state.houseboat });
        }

        if (get(this.props, 'permit.components.emergency_contact')) {
            item = Object.assign(item, {
                emergency_contact: this.state.emergencyContact,
            });
        }

        // Yellowstone Snowmobile is the only such case in the entire permits world so we are going to hard code it here
        // to resolve R1S-33685. Typically lottery awards don't have anything to do with itineraries but in this singular case
        // it does. So this makes the most sense. Future me, don't judge present me because you have the luxury of hindsight
        // while I just need to get this sh*t done
        if (!item.itinerary && item.permit_id === types.YELLOWSTONE_SNOWMOBILE_ID) {
            item = Object.assign(item, {
                itinerary: [
                    {
                        division_id: item.division_id,
                        end_date: item.end_date,
                        start_date: item.start_date,
                        exclude_last_day: false,
                    },
                ],
            });
        }

        item = pActions.prepareIssuance(item);
        return item;
    };

    onChangeDetailsClick = () => {
        const reservationId = `${this.props.issuance.id}${types.RES_ID_DELIMITER}${this.props.permit.id}`;
        let url = `/permits/${this.props.permit.id}/registration/detailed-availability`;

        const query = {};
        if (reservationId) {
            // this is the permit_section_filter
            query.reservationId = reservationId;
        }
        if (this.props.issuance.start_date) {
            query.date = this.props.issuance.start_date;
        }

        // TODO use encodeQueryParams
        const queryString = Object.keys(query)
            .map((key) => `${encodeURIComponent(key)}=${encodeURIComponent(query[key])}`)
            .join('&');
        if (queryString) {
            url = `${url}?${queryString}`;
        }
        this.props.router.navigate(url);
    };

    disableAnimalList() {
        // Enforce rule that prohibits bringing animals on overnight stays
        const disableOvernightStockRule = hasRule(
            types.DISALLOW_OVERNIGHT_STOCK,
            this.props.permit.rules
        );
        const disableStockListRule = hasRule(
            types.DISABLE_STOCK_LIST,
            this.props.permit.rules,
            this.state.division.id
        );

        if (disableOvernightStockRule) {
            const nightsSelected =
                dateDiffs(
                    this.state.launchDetail.entryDate,
                    this.state.launchDetail.exitDate
                ) - 1;
            if (nightsSelected > 0) {
                // if this is an overnight stay, return true (to disable animal list) and clear the animal list data
                return true;
            }
        } else if (disableStockListRule) {
            return true;
        }
        // otherwise, return false (to enable animal list) if there is no rule OR there is a rule but not an overnight stay
        return false;
    }

    updateStateGroupDetail = (id, path, value) =>
        this.setState(({ groupDetail }) => ({
            groupDetail: set({ ...groupDetail }, path, value),
        }));

    shouldRenderAlternatePlaceHolders = () =>
        !!get(this.props.permit, 'components.alternate_group_leader');

    renderAlternatePlaceHolders() {
        const { issuance, permit } = this.props;
        const { groupDetail } = this.state;

        const disabled =
            issuance.status === issuanceStatuses.get(types.PERMIT_ISSUED) ||
            issuance.status === issuanceStatuses.get(types.PERMIT_COMPLETE) ||
            issuance.status === issuanceStatuses.get(types.LOTTERY_AWARDED);

        // Create custom data bindings for core permits specific paths
        const paths = {
            email: 'email',
            last_name: 'last_name',
            first_name: 'first_name',
            alt_group_detail: dataBindings[GROUP_DETAIL_ALT_PROP_PATH],
        };

        return hasRule('SimpleAlternateLeader', permit.rules, issuance.division_id)
            ? this.renderSimpleAlternatePermitHolders(
                  issuance,
                  permit,
                  groupDetail,
                  disabled,
                  paths
              )
            : this.renderAlternatePermitHolders(
                  issuance,
                  permit,
                  groupDetail,
                  disabled,
                  paths
              );
    }

    renderAlternatePermitHolders = (issuance, permit, groupDetail, disabled, paths) => {
        const alternateModalSections = [
            {
                title: 'Why An Alternate Permit Holder?',
                paragraph:
                    'The permit holder is required to be present on the entire trip for this permit to be valid. In the event that the permit holder is unavailable, alternate permit holders can be designated at the time of reservation.',
            },
            {
                title: 'How to Add an Alternate Permit Holder',
                paragraph: (
                    <>
                        Enter an email address for an alternate permit holder.The
                        alternate permit holder must respond and accept being an alternate
                        within <strong> 72 hours </strong>
                        or this permit will not have an alternate.If the alternate does
                        not have a recreation.gov account, they will be required to create
                        an account.
                    </>
                ),
            },
            {
                title: 'Alternate Permit Holder Considerations',
                paragraph:
                    'Being an alternate carries the same responsibilities as the primary permit holder. If there are seasonal reservations restrictions, these will apply to the alternate as if they are the primary permit holder. In other words, an alternate may not be able to reserve another permit or be an alternate for another permit at this facility for this season.',
            },
        ];

        return (
            <AlternatePermitHolder
                alternateMessageDetails={
                    <>
                        Please enter a valid email address for each of the intended
                        holders. They will receive an email and will need to validate
                        within <strong>72 hours</strong>.
                    </>
                }
                alternateModalSections={alternateModalSections}
                displayValidationError
                hasPermission
                isInternal={false}
                isReadOnly={disabled}
                pActions={{
                    ...pActions,
                    formValidationChange: Function.prototype,
                    updatePendingReservation: this.updateStateGroupDetail,
                }}
                permit={permit}
                reservation={{
                    ...groupDetail,
                    id: this.props.router.params.reservationId,
                    sales_channel_type: 0,
                    status: issuance.status,
                }}
                validation={this.state.phValidations}
                paths={paths}
            />
        );
    };

    renderSimpleAlternatePermitHolders = (
        issuance,
        permit,
        groupDetail,
        disabled,
        paths
    ) => {
        const alternateModalSections = [
            {
                title: 'Why An Alternate Permit Holder?',
                paragraph:
                    'The permit holder is required to be present on the entire trip for this permit to be valid. In the event that the permit holder is unavailable, alternate permit holders can be designated at the time of reservation.',
            },
            {
                title: 'How to Add an Alternate Permit Holder',
                paragraph: <p>Enter a name for an alternate permit holder.</p>,
            },
        ];

        return (
            <SimpleAlternatePermitHolder
                alternateMessageDetails={
                    <p>Please enter a name for each of the intended holders.</p>
                }
                alternateModalSections={alternateModalSections}
                displayValidationError
                hasPermission
                isInternal={false}
                isReadOnly={disabled}
                pActions={{
                    ...pActions,
                    formValidationChange: Function.prototype,
                    updatePendingReservation: this.updateStateGroupDetail,
                }}
                permit={permit}
                reservation={{
                    ...groupDetail,
                    id: this.props.router.params.reservationId,
                    sales_channel_type: 0,
                    status: issuance.status,
                }}
                validation={this.state.phValidations}
                paths={paths}
            />
        );
    };

    renderPermitHolderIdentification() {
        if (get(this.props, 'permit.components.permit_holder_age')) {
            return (
                <PermitHolderIdentification
                    groupMembers={this.state.groupMembers}
                    handleGroupMemberChange={this.handleGroupMemberChange}
                    collectAge
                />
            );
        }
        if (get(this.props, 'permit.components.permit_holder_birthdate')) {
            return (
                <PermitHolderIdentification
                    groupMembers={this.state.groupMembers}
                    handleGroupMemberChange={this.handleGroupMemberChange}
                    collectBirthdate
                />
            );
        }
        return null;
    }

    renderCommercialGuides() {
        const { guides, guidesCountTowardsQuota } = this.state;
        const account = SecurityHelper.getAccount();

        if (
            !account.is_commercial_account ||
            (guidesCountTowardsQuota && !get(guides, 'length')) ||
            types.ANGELS_LANDING_FACILITIES.includes(this.props.permit.id)
        ) {
            return null;
        }

        return (
            <CommercialGuideList
                allGuides={this.props.all_guides}
                guides={guides}
                disableListSizeChange={guidesCountTowardsQuota}
                handleGuidesChange={this.handleGuidesChange}
            />
        );
    }

    renderLaunchPointDetail() {
        if (
            !this.props.permit.components ||
            !(
                this.props.permit.components.entry_exit_points ||
                this.props.permit.components.entry_points
            )
        ) {
            return null;
        }

        return (
            <LaunchPointDetail
                entryOnly={this.props.permit.components.entry_points}
                launchWindows={{
                    display: this.props.permit.components.launch_time,
                    options: getLaunchWindowOptions(this.props.permit),
                    selected: this.state.launchDetail.launchTime,
                }}
                entrances={this.state.validEntrances}
                exits={this.state.validExits}
                launchDetail={this.state.launchDetail}
                validation={this.state.validation}
                handleLaunchDetailChange={this.handleLaunchDetailChange}
                permitCategory={this.props.permit.category}
                issuance={this.props.issuance}
            />
        );
    }

    renderGroupDetail() {
        const { groupDetail } = this.state;

        return (
            <GroupDetailNew
                displayValidationError
                hasPermission
                pActions={{
                    ...pActions,
                    formValidationChange: Function.prototype,
                    updatePendingReservation: this.updateStateGroupDetail,
                }}
                reservation={{
                    ...groupDetail,
                    sales_channel_type: 0,
                    id: this.props.router.params.reservationId,
                }}
                validation={this.state.phValidations}
            />
        );
    }

    renderTripDates() {
        const { permit, issuance, stayLimit } = this.props;
        const { launchDetail, division } = this.state;
        const applicableQuotas = [
            types.GROUP_AVAILABILITY_KEY_NAME,
            types.MEMBER_AVAILABILITY_KEY_NAME,
        ];
        let isDisabled = true;

        for (let i = 0; i < applicableQuotas.length; i++) {
            if (hasRule(applicableQuotas[i], permit.rules, issuance.division_id)) {
                isDisabled = false;
                break;
            }
        }

        // check if issuance is from a lottery win, if so, check if dates should be locked/disabled from user input
        if (issuance.lottery_awarded) {
            const isDurationSet =
                get(issuance, 'selected_lottery_preference.length_of_stay', 0) > 0;
            isDisabled = isDisabled || isDurationSet;
        }

        if (isDisabled) {
            return null;
        }
        const campsiteAssignments = get(issuance, 'campsite_reservations');
        if (campsiteAssignments) {
            isDisabled = isCampsiteAssigned(campsiteAssignments);
        }

        const stayMinimum = parseInt(
            readRuleValue('StayMinimum', permit.rules, issuance.division_id),
            10
        );
        const isLottery =
            this.props.issuance.lottery_awarded || this.props.issuance.lottery_accepted;

        return (
            <PermitTripDates
                isLottery={isLottery}
                permit={permit}
                launchDetail={launchDetail}
                handleLaunchDetailChange={this.handleLaunchDetailChange}
                handleChangeQuotaDetails={this.redirectToAvailabilityPage}
                validation={this.state.validation}
                divisionId={division.id}
                stayLimit={stayLimit}
                stayMinimum={stayMinimum}
                reservationId={this.props.router.params.reservationId}
                isDisabled={isDisabled}
                isQuotaModifyAllowed={this.isQuotaModifyAllowed()}
            />
        );
    }

    renderEquipmentList() {
        if (!this.props.permit.components) {
            return null;
        }
        return [
            this.renderVehicleList('vehicle-list'),
            this.renderWatercraftList('watercraft-list'),
            this.renderSnowmobiles('snowmobiles-list'),
            this.renderStockList('stock-list'),
            this.renderPetList('pet-list'),
        ];
    }

    renderVehicleList(key) {
        if (!this.props.permit.components || !this.props.permit.components.vehicle_list) {
            return null;
        }

        if (!this.props.permit.components.vehicle_skip_entrance) {
            if (
                !entranceHasParking(
                    this.state.launchDetail.entryLocationId,
                    this.props.permit.entrances
                )
            ) {
                return null;
            }
        }

        const maxVehicles = readRuleValue(
            types.MAX_VEHICLES_KEY_NAME,
            this.props.permit.rules
        );

        return (
            <VehicleList
                key={key}
                maxVehicles={maxVehicles}
                vehicles={this.state.vehicles}
                handleVehiclesChange={this.handleVehiclesChange}
                descriptionRequired={
                    !!this.state.validation_rules[types.IsVehicleDescriptionEmpty]
                }
                licensePlateRequired={
                    !!this.state.validation_rules[types.IsLicencePlateEmpty]
                }
                licenseStateRequired={
                    !!this.state.validation_rules[types.IsLicenseStateEmpty]
                }
            />
        );
    }

    renderWatercraftList(key) {
        const hasWatercraftComponentRule = hasRule(
            SharedConstants.WATERCRAFT_FOR_DIVISION,
            this.props.permit.rules,
            this.state.division.id
        );
        if (
            !hasWatercraftComponentRule &&
            !get(this.props, 'permit.components.watercraft_list') &&
            !get(this.props, 'permit.components.watercraft_details')
        ) {
            return null;
        }

        if (get(this.props, 'permit.components.watercraft_details', false)) {
            return (
                <GenericCommentBox
                    key={key}
                    handleTextChange={this.handleWatercraftDetailsChange}
                    text={this.state.watercraftDetails}
                    title="Watercraft Details"
                    instructionText="Please provide the make, model, length and Vessel Registration Number for the watercraft. You must have a separate permit for each boat."
                    placeholderText="Enter your watercraft details here..."
                    isRequired={
                        !isEmpty(
                            this.state.validation_rules[types.IS_WATERCRAFT_DETAIL_EMPTY]
                        )
                    }
                    validation={this.state.validation}
                    validationTarget={types.IS_WATERCRAFT_DETAIL_EMPTY}
                />
            );
        }

        const hasWatercraftQuota = hasRule(
            SharedConstants.WATERCRAFT_AVAILABILITY_KEY_NAME,
            this.props.permit.rules,
            this.state.division.id
        );
        if (this.props.permit.components.watercraft_pere_marquette) {
            return (
                <FixedWatercraftList
                    key={key}
                    watercrafts={this.props.issuance.equipments}
                    hasWatercraftQuota={hasWatercraftQuota}
                    permitId={this.props.permit.id}
                    issuanceId={this.props.issuance.id}
                    issuanceStartDate={this.props.issuance.start_date}
                    isQuotaModifyAllowed={this.isQuotaModifyAllowed()}
                />
            );
        }
        return (
            <WatercraftList
                key={key}
                permit={this.props.permit}
                watercrafts={this.state.watercrafts}
                handleWatercraftsChange={this.handleWatercraftsChange}
                watercraftsCount={this.state.watercraftsCount}
                validation={this.state.validation}
                maxQuota={this.state.maxWatercraftQuota}
                hasWatercraftQuota={hasWatercraftQuota}
                issuanceId={this.props.issuance.id}
                issuanceStartDate={this.props.issuance.start_date}
                isQuotaModifyAllowed={this.isQuotaModifyAllowed()}
            />
        );
    }

    renderSnowmobiles(key) {
        if (
            !this.props.permit ||
            !this.props.permit.components ||
            !this.props.permit.components.snowmobile
        ) {
            return null;
        }

        const maxSnowmobiles = readRuleValue(
            'MaxEquipmentPerReservation',
            this.props.permit.rules
        );

        return (
            <SnowmobileCounter
                key={key}
                snowmobiles={this.state.snowmobiles}
                maxSnowmobiles={maxSnowmobiles}
                handleSnowmobilesChange={this.handleSnowmobilesChange}
                showIsRequired={
                    !!getValidationErrorStyle(
                        this.state.validation,
                        types.IS_SNOWMOBILE_EMPTY
                    )
                }
            />
        );
    }

    renderStockList(key) {
        if (
            !this.props.permit ||
            !this.props.permit.components ||
            !this.props.permit.components.animal_list
        ) {
            return null;
        }

        const disableAnimalList = this.disableAnimalList();
        if (!disableAnimalList) {
            return (
                <GenericList
                    key={key}
                    permit={this.props.permit}
                    items={this.state.animals}
                    handleListChange={this.handleAnimalsChange}
                    maxQuota={this.getRemainingStockAllowed()}
                    total={this.state.animalsTotal}
                    listType="stock"
                    options={this.props.permit.animals.map((a) => ({
                        value: a.type.toString(),
                        label: animalTypeToString.get(a.type),
                    }))}
                />
            );
        }

        const disableStockListRule = hasRule(
            types.DISABLE_STOCK_LIST,
            this.props.permit.rules,
            this.state.division.id
        );
        if (!disableStockListRule) {
            return (
                <div className="per-equipment-form per-section-group" key={key}>
                    <div className="rec-order-detail-subsection-title">Stock</div>
                    <div className="rec-notification-warning">
                        <span className="rec-notification-icon">
                            <Icon iconName="warning" className="rec-icon-warning" />
                        </span>
                        <div className="rec-notification-body">
                            <p>You cannot bring stock for an overnight trip.</p>
                        </div>
                    </div>
                </div>
            );
        }
        return null;
    }

    renderPetList(key) {
        const disablePetListRule = hasRule(
            types.DISABLE_PET_LIST,
            this.props.permit.rules,
            this.state.division.id
        );
        if (!get(this.props, 'permit.components.pet_list') || disablePetListRule) {
            return null;
        }

        return (
            <GenericList
                key={key}
                items={this.state.pets}
                handleListChange={this.handlePetsChange}
                permit={this.props.permit}
                maxQuota={this.getRemainingPetsAllowed()}
                total={this.state.petsTotal}
                listType="pet"
                options={this.props.permit.pets.map((p) => ({
                    value: p.type.toString(),
                    label: animalTypeToString.get(p.type),
                }))}
            />
        );
    }

    renderIssueStationDetail() {
        // Hide the Issuing Station section entirely for Middle Fork of the Salmon
        // (234623) and Salmon River (234622).
        const hideIssueStationFacilities = [types.MIDDLE_FORK_ID, types.SALMON_RIVER_ID];
        if (hideIssueStationFacilities.includes(get(this.props, 'permit.id'))) {
            return null;
        }
        if (this.props.permit) {
            return (
                <IssueStationDetail
                    issueStations={this.state.validIssueStations}
                    issueStationDetail={this.state.issueStationDetail}
                    validation={this.state.validation}
                    handleIssueStationDetailChange={this.handleIssueStationDetailChange}
                    rules={get(this.props, 'permit.rules', [])}
                />
            );
        }
        return null;
    }

    renderCampsiteRequestsDetail() {
        const { permit, issuance, campsites } = this.props;

        // do not show if missing data required to read relevant rule
        if (!permit || !issuance || !campsites) {
            return null;
        }

        // do not show if rule doesn't exist at all
        const { division_id: divisionId } = issuance;
        if (!hasRule('CampsiteItinerary', permit.rules, divisionId)) {
            return null;
        }

        const campsiteItineraryRule = readRule(
            'CampsiteItinerary',
            permit.rules,
            divisionId
        );
        let displayRule;
        try {
            displayRule = JSON.parse(campsiteItineraryRule.metadata);
        } catch (e) {
            return null;
        }

        // do not show if rule's metadata prohibits display on external UI
        if (!displayRule.external_control.write) {
            return null;
        }

        let divisionCampsites = [];
        campsites.forEach((campsite) => {
            if (campsite.division_ids.includes(divisionId)) {
                divisionCampsites.push(campsite);
            }
        });

        if (!divisionCampsites.length) {
            return null;
        }

        divisionCampsites = sortBy(divisionCampsites, ['view_order']);

        const dates = [];
        const nightDates = nightsInRange(
            this.state.launchDetail.entryDate,
            this.state.launchDetail.exitDate
        );
        const nights = nightDates.length;

        if (nights > 0) {
            for (let i = 0; i < nights; i++) {
                const displayDate = nightDates[i]
                    .utc()
                    .format(types.MONTH_DAY_YEAR_FORMAT);
                dates.push(displayDate);
            }
        }

        const campsiteAssignments = get(issuance, 'campsite_reservations');
        const shouldDisablePreferences = isCampsiteAssigned(campsiteAssignments);

        return (
            <PreferenceDetail
                choices={divisionCampsites}
                dates={dates}
                nights={nights}
                disablePreferences={shouldDisablePreferences}
                preferences={this.state.campsitePreferences}
                handlePreferenceChange={this.handlePreferenceChange}
                permitCategory={this.props.permit.category}
                permitId={permit.id}
            />
        );
    }

    renderHouseboatDetail() {
        if (this.props.permit.components.houseboat_list) {
            return (
                <HouseboatDetail
                    handleHouseboatChange={this.handleHouseboatChange}
                    houseboat={this.state.houseboat}
                    houseboatOptions={this.state.division.houseboats}
                    isPrivateHouseboat={this.state.isPrivateHouseboat}
                />
            );
        }
        return null;
    }

    renderEmergencyContact() {
        if (this.props.permit.components.emergency_contact) {
            return (
                <EmergencyContact
                    validation={this.state.validation}
                    validationRequired={this.state.validation_rules}
                    handleEmergencyContactChange={this.handleEmergencyContactChange}
                    contact={this.state.emergencyContact}
                />
            );
        }
        return null;
    }

    renderTravelMethod() {
        if (this.props.permit.components.travel_method_list) {
            return (
                <TravelMethod
                    handleTravelMethodChange={this.handleTravelMethodChange}
                    selectedType={this.state.travelMethodType}
                    travelMethods={this.props.permit.travel_methods}
                />
            );
        }
        return null;
    }

    renderPriorExperience() {
        if (this.props.permit.components.prior_experience) {
            return (
                <GenericCommentBox
                    handleTextChange={this.handlePriorExperienceChange}
                    text={this.state.priorExperience}
                    title="Prior Experience"
                    instructionText="Please provide prior river experience of Permit Holder and/or Trip Leader here. Please include prior dates of Class III+ Rivers rowed and river names."
                    placeholderText="Enter your prior experience here..."
                    validation={this.state.validation}
                    validationTarget={types.IS_PRIOR_EXPERIENCE_EMPTY}
                    isRequired={
                        !isEmpty(
                            this.state.validation_rules[types.IS_PRIOR_EXPERIENCE_EMPTY]
                        )
                    }
                />
            );
        }

        return null;
    }

    renderCommercialGuideQuestion() {
        const account = SecurityHelper.getAccount();
        if (
            account.is_commercial_account ||
            !this.props.permit.components.commercial_guide_question
        ) {
            return null;
        }

        return (
            <GuideDetails
                isCommerciallyGuided={this.state.isCommerciallyGuided}
                handleIsGuidedChange={this.handleIsCommerciallyGuidedChange}
                guideDetails={get(this.state, 'guides[0]', {})}
                guideValidations={this.state.guideValidations}
                required={this.state.validation_rules[types.IsCommerciallyGuidedEmpty]}
                permitId={this.props.permit.id}
            />
        );
    }

    renderGenericCommentsBox() {
        const instructionText = get(
            this.props,
            'permit.description[comment_box_description]',
            'Add any important notes or special instructions for your trip.'
        );

        return (
            <GenericCommentBox
                handleTextChange={this.handleCommentChange}
                text={this.state.comment}
                title="Comments"
                instructionText={instructionText}
                placeholderText="Add your comments here..."
                isRequired={
                    !isEmpty(this.state.validation_rules[types.IS_COMMENTS_EMPTY])
                }
                validationTarget={types.IS_COMMENTS_EMPTY}
            />
        );
    }

    onModifyGroupSizeSuccess = (newGroupSize) => {
        // modify "original" issuance in redux store
        const newIssuance = modifyIssuanceGroupSize(this.props.issuance, newGroupSize);
        this.props.dispatch(pActions.setIssuanceAfterChangeGroupSize(newIssuance));

        // update groupMembers and related state values (+validations)
        this.handleGroupMemberChange(
            convertIssuanceToGroupMembersObj(
                getResizedGroupInfo(
                    {
                        ...this.props.issuance,
                        ...this.fetchMembers(this.state.groupMembers),
                    },
                    newGroupSize
                )
            ),
            true,
            () => {
                this.updateRulesState(this.props);
                this.setState({
                    isModifyGroupSizeOpen: false,
                    isModifyDesolation:
                        this.props.permit.id === types.DESOLATION_WILDERNESS_ID,
                });
            }
        );
    };

    onCloseModifyGroupSizeModal = () => {
        this.setState({ isModifyGroupSizeOpen: false });
    };

    handleProceedToCart = (opts) => this.addToCart(opts);

    handleContinue = () => this.addToCart();

    render() {
        const { cart, issuance, permit } = this.props;
        const { division } = this.state;
        const isLoading =
            !permit ||
            !issuance ||
            this.props.router.params.reservationId !== `${issuance.id}__${permit.id}` ||
            (get(this.props, 'issuance.status') ===
                issuanceStatuses.get(types.LOTTERY_AWARDED) &&
                isInvalidLotteryAward(this.props));

        if (isLoading) {
            return (
                <div className="permit-loading-wrapper">
                    <Spinner />
                </div>
            );
        }

        // @todo add country validation in backend
        const permitValidations = [
            ...permit.validations,
            {
                action: 'isFieldEmpty',
                group: 'Registration',
                message: 'Permit holder country is required',
                modified_time: '0001-01-01T00:00:00Z',
                name: 'IsPermitHolderCountry',
                permit_id: permit.id,
                target: 'permitHolderCountry',
                type: 1,
                value: '-1',
            },
        ];
        const registrationValidation = runValidations(
            this.props.dispatch,
            permitValidations,
            this.state,
            types.REGISTRATION,
            undefined,
            get(this.props, 'issuance.permit_type', '')
        );
        const permitRegistrationValidation = runValidations(
            this.props.dispatch,
            permitValidations,
            this.state,
            types.PERMIT_REGISTRATION,
            undefined,
            get(this.props, 'issuance.permit_type', '')
        );
        const validations = registrationValidation.concat(permitRegistrationValidation);

        const isCommercial = SecurityHelper.isCommercialAccount();

        const passOptions = loadPassInfo(permit, this.props.flags, { isCommercial });

        const buttonLabels = setButtonLabels(issuance, false, this.state.amountDue);
        if (this.state.fetching) {
            buttonLabels.proceed = 'Loading...';
        }

        const excludeHolderFromList = hasRule(
            'GroupListWithoutHolder',
            permit.rules,
            division.id
        );
        const disableGroupSizeChanges =
            (hasRule('QuotaUsageByMember', permit.rules, division.id) ||
                hasRule('QuotaUsageByMemberDaily', permit.rules, division.id)) &&
            !hasRule(types.REDUCIBLE_GROUP_SIZE_KEY_NAME, permit.rules, division.id);
        const disableGroupList = hasRule('DisableGroupList', permit.rules, division.id);
        const hasTripDatesLocked =
            hasRule(types.GROUP_AVAILABILITY_DAILY_KEY_NAME, permit.rules, division.id) ||
            hasRule(
                types.MEMBER_AVAILABILITY_DAILY_KEY_NAME,
                permit.rules,
                division.id
            ) ||
            hasRule(types.WATERCRAFT_AVAILABILITY_KEY_NAME, permit.rules, division.id);

        const minMember =
            parseInt(readRuleValue('MinGroupSize', permit.rules, division.id), 10) || 0;

        const errorsList = errorsToStringList([
            this.props.issuanceError,
            this.props.fetchError,
            this.props.cartError,
        ]);

        const isHighUseReservation =
            getSeasonality(
                this.props.aggSeasonRules,
                permit.time_zone,
                division.id,
                issuance.start_date
            ) === SEASONALITY_HIGHUSESEASON;
        const feeCalculationModel = get(this.state, 'division.fee_calculation_model', 0);
        const isHighUseFeeApplicable =
            feeCalculationModels.get(types.PER_MEMBER_PER_DAY_BY_SEASON) ===
                feeCalculationModel && isHighUseReservation;

        const isPermitWithCommentBox = ![
            types.MT_ST_HELENS_ID,
            types.HALF_DOME_ID,
        ].includes(permit.id);

        // if has the rules & a non-purchased permit...
        const readOnlyGroupList =
            this.state.disableModifyGroupList &&
            issuance.status !== issuanceStatuses.get(types.PERMIT_PRELIMINARY) &&
            issuance.status !== issuanceStatuses.get(types.PERMIT_IN_PROGRESS);
        const isIssuedPermit =
            issuance.status === issuanceStatuses.get(types.PERMIT_ISSUED);

        const commercialText = isCommercial ? ` (${types.COMMERCIAL})` : '';
        const isDesoChangeValidChargeRefund =
            this.state.isModifyDesolation && this.state.amountDue <= 0;

        const summary = (
            <OrderDetailsSummary
                isLoading={isLoading}
                heading={`${this.getDivision().name}${commercialText}`}
                imageUrl={this.props.imageUrl}
                isWaitingForServerResponse={this.state.fetching}
                parentName={this.getName()}
                isProceedToCartDisabled={isIssuedPermit || isDesoChangeValidChargeRefund}
                onProceedToCartClick={this.handleProceedToCart}
                proceedToCartButtonText={buttonLabels.proceed}
                secondaryButtonText={buttonLabels.continue}
                secondaryButtonIsDisabled={isIssuedPermit}
                onSecondaryButtonClick={this.handleContinue}
                showBuyNow
                reservationId={this.props.router.params.reservationId}
            >
                <ReservationDetail
                    lineItems={this.state.lineItems}
                    issuance={this.createIssuanceObject()}
                    name={get(this.props, 'permit.name', '')}
                    permit={permit}
                    division={division}
                    itinerary={issuance.itinerary}
                    entryDate={this.state.launchDetail.entryDate}
                    exitDate={this.state.launchDetail.exitDate}
                    isExtended={this.state.launchDetail.isExtended}
                    groupMembers={this.state.groupMembers}
                    guides={this.state.guides}
                    watercrafts={this.state.watercrafts}
                    animals={this.state.animals}
                    addToCart={this.addToCart}
                    amountPaid={issuance.amount_paid}
                    removeFromCart={this.removeFromCart}
                    recareaName={getRecareaName(this.props.recarea)}
                    issuanceStatus={issuance.status}
                    campsitePreferences={this.state.campsitePreferences}
                    isQuotaModifyAllowed={this.isQuotaModifyAllowed()}
                    hasTripDatesLocked={hasTripDatesLocked}
                    buttonLabels={buttonLabels}
                    disableButtons={isIssuedPermit}
                    isHighUseFeeApplicable={isHighUseFeeApplicable}
                    isCommercialUser={isCommercial}
                    reservationDetailName={this.getDivision().name}
                    permitMapping={this.props.permitMapping}
                />
            </OrderDetailsSummary>
        );
        return (
            <div className="rec-order-detail-page per-registration-page">
                <ModifyGroupSizeModalContainer
                    onSuccess={this.onModifyGroupSizeSuccess}
                    onRequestClose={this.onCloseModifyGroupSizeModal}
                    isOpen={this.state.isModifyGroupSizeOpen}
                />
                <Box background="gray-2" paddingY="sm">
                    <ProgressIndicator
                        eventToReportToGA={gaReportingCartReservationsForStepper(
                            cart?.reservations,
                            PROGRESS_STEPS,
                            0
                        )}
                        shouldReportToGA
                        steps={PROGRESS_STEPS}
                        activeStep={1}
                    />
                </Box>
                <Spacer size="xl" />
                <ContentBlock>
                    <OrderDetailsLayout summaryElement={summary}>
                        {validations}
                        <div
                            tabIndex="-1"
                            style={{ outline: 'none' }}
                            ref={this.errorsRef}
                            aria-live="polite"
                        >
                            {!(isEmpty(this.state.errors) && isEmpty(errorsList)) && (
                                <Alert
                                    className="permit-error-notification"
                                    key={getNewId()}
                                    type="error"
                                    heading="We're working to get this reservation booked for you, but there is an issue with some necessary information. Please take a moment to update the information flagged below and try again."
                                    hasAriaLive
                                >
                                    <ul>
                                        {this.state.errors.map((err) => (
                                            <li key={getNewId()}>{err}</li>
                                        ))}
                                        {errorsList.map((err) => (
                                            <li key={getNewId()}>
                                                <div className="permit-validation">
                                                    {err}
                                                </div>
                                            </li>
                                        ))}
                                    </ul>
                                </Alert>
                            )}
                        </div>
                        <Heading headingLevel={1} appearance="h3">
                            Order Details
                        </Heading>
                        <CSRFacilityInformation
                            inventory="Permit"
                            facility_id={permit.id}
                        />
                        <OrderDetailsSection
                            heading={permit.name}
                            onRemoveClick={!isIssuedPermit ? this.removeFromCart : null}
                            onModifyClick={
                                hasTripDatesLocked && this.isQuotaModifyAllowed()
                                    ? this.onChangeDetailsClick
                                    : null
                            }
                            isLoading={isLoading}
                        >
                            <form id="form-name">
                                <fieldset
                                    disabled={
                                        issuance.status ===
                                        issuanceStatuses.get(types.PERMIT_ISSUED)
                                    }
                                >
                                    {this.renderGroupDetail()}
                                    {this.shouldRenderAlternatePlaceHolders() &&
                                        this.renderAlternatePlaceHolders()}
                                    {this.renderPermitHolderIdentification()}
                                    {this.renderCommercialGuides()}
                                    {this.renderLaunchPointDetail()}
                                    {this.renderTripDates()}
                                    {this.renderCampsiteRequestsDetail()}
                                    {this.renderHouseboatDetail()}
                                    {this.renderEmergencyContact()}
                                    <GroupMemberDetail
                                        permit={permit}
                                        groupMembers={this.state.groupMembers}
                                        handleGroupMemberChange={
                                            this.handleGroupMemberChange
                                        }
                                        launchDetail={this.state.launchDetail}
                                        minMemberQuota={minMember}
                                        maxMemberQuota={this.getRemainingMembersAllowed()}
                                        maxPetQuota={this.state.maxPetQuota}
                                        validation={this.state.validation}
                                        divisionId={this.getDivision().id}
                                        passOptions={passOptions}
                                        issuance={issuance}
                                        hasSecureQuotaText={
                                            disableGroupList || disableGroupSizeChanges
                                        }
                                        excludeHolderFromList={
                                            excludeHolderFromList || isCommercial
                                        }
                                        disableGroupList={disableGroupList}
                                        disableGroupSizeChanges={disableGroupSizeChanges}
                                        isCommercialAccount={isCommercial}
                                        isQuotaModifyAllowed={this.isQuotaModifyAllowed()}
                                        onModifyDetails={this.onModifyDetails}
                                        modifyDetailsButtonText="Modify Group Size"
                                        refreshValidations={this.isThisFormValid}
                                        validationRules={this.state.validation_rules}
                                        maxGroupSize={this.state.maxGroupSize}
                                        handleChangeQuotaDetails={
                                            this.redirectToAvailabilityPage
                                        }
                                        maxGroupSizeCommercial={
                                            this.state.maxGroupSizeCommercial
                                        }
                                        isHighUseFeeApplicable={isHighUseFeeApplicable}
                                        readOnly={readOnlyGroupList}
                                    />
                                    {this.renderEquipmentList()}
                                    {this.renderPriorExperience()}
                                    {this.renderTravelMethod()}
                                    {this.renderIssueStationDetail()}
                                    {this.renderCommercialGuideQuestion()}
                                    {isPermitWithCommentBox &&
                                        this.renderGenericCommentsBox()}
                                </fieldset>
                            </form>
                        </OrderDetailsSection>
                        <section>
                            <NeedToKnow
                                siteName={permit.name}
                                siteInfo={get(permit, 'description.need_to_know_order')}
                                isChecked={this.state.needToKnowChecked}
                                onChange={this.handleNeedToKnowCheckboxChange}
                            />
                        </section>
                    </OrderDetailsLayout>
                </ContentBlock>
            </div>
        );
    }
}

function select(state) {
    return {
        permit: state.permit.result,
        issuance: state.permit.issuance,
        lottery: state.permit.lottery,
        updateIssuanceResult: state.permit.update_issuance_result,
        updatedIssuance: state.permit.updated_issuance,
        issuanceError: state.permit.update_issuance_error,
        recarea: state.permit.recarea,
        recareaError: state.permit.recarea_err,
        fetchError: state.permit.fetch_err,
        feeDiff: state.permit.issuance_fee_difference,
        feeDiffError: state.permit.issuance_fee_difference_err,
        permitValidation: state.permitValidation.results,
        cart: state.permit.cart,
        cartError: state.permit.cart_err,
        campsites: state.permit.campsites,
        filters: state.permit.filters,
        stayLimit: state.permit.rule_staylimit,
        all_guides: state.permit.account_employees,
        aggSeasonRules: state.permit.agg_season_rules,
        imageUrl: sharedSelectors.getImageUrlSelector(state),
        permitMapping: permitContentSelector.permitMapping(state),
    };
}

export default withCompatRouter(
    connect(select)(withLDConsumer()(PermitRegistrationPage))
);
